import React from 'react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { Table } from '@tanstack/react-table'
import UiTableColumnVisibilityItems from 'components/Ui/UiTableColumnVisibilityToggleComponents/UiTableColumnVisibilityItems'
import { useTableColumnVisibilityDraft } from './useTableColumnVisibilityDraft'

interface UiTableColumnVisibilityPanelProps<TData> {
    table: Table<TData>
    enabled: boolean
    showLoadedViewLabel?: boolean
    onApplied?: () => void
}

const UiTableColumnVisibilityPanel = <TData,>({
    table,
    enabled,
    showLoadedViewLabel = true,
    onApplied,
}: UiTableColumnVisibilityPanelProps<TData>) => {
    const { t } = useTranslation()
    const {
        allowedColumns,
        draftVisibility,
        draftOrder,
        getColumnLabel,
        toggleDraftVisibility,
        checkAllDraft,
        uncheckAllDraft,
        allColumnsVisible,
        someColumnsVisible,
        hasDraftChanges,
        applyDraft,
        resetDraft,
        scrollContainerRef,
        activeViewName,
        setDraftOrder,
    } = useTableColumnVisibilityDraft(table, enabled)

    if (!enabled) {
        return null
    }

    return (
        <>
            <div className="flex items-center justify-between gap-2 px-2 pb-2">
                <div className="flex items-center gap-2 text-xs">
                    {!allColumnsVisible && (
                        <button
                            type="button"
                            className="font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                            onClick={checkAllDraft}
                        >
                            {t('Check all')}
                        </button>
                    )}
                    {!allColumnsVisible && someColumnsVisible && (
                        <span className="text-gray-300 dark:text-gray-600">|</span>
                    )}
                    {someColumnsVisible && (
                        <button
                            type="button"
                            className="font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                            onClick={uncheckAllDraft}
                        >
                            {t('Uncheck all')}
                        </button>
                    )}
                </div>
            </div>
            <div ref={scrollContainerRef} className="p-1 max-h-56 overflow-y-auto border-t border-gray-100 dark:border-gray-800">
                {showLoadedViewLabel && (
                    <div className="px-2 pb-2 text-xs text-gray-500 dark:text-gray-400">
                        {activeViewName
                            ? `${t('Loaded view')}: ${activeViewName}`
                            : `${t('Loaded view')}: -`}
                    </div>
                )}
                <UiTableColumnVisibilityItems
                    columns={allowedColumns}
                    draftVisibility={draftVisibility}
                    orderedColumnIds={draftOrder}
                    getColumnLabel={getColumnLabel}
                    onToggleVisibility={toggleDraftVisibility}
                    onColumnOrderChange={setDraftOrder}
                    scrollContainerRef={scrollContainerRef}
                />
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-gray-200 dark:border-gray-800 px-2 py-2">
                <button
                    type="button"
                    className={clsx(
                        'rounded-md px-2.5 py-1 text-xs font-medium',
                        hasDraftChanges
                            ? 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
                            : 'text-gray-400 cursor-not-allowed dark:text-gray-600'
                    )}
                    onClick={resetDraft}
                    disabled={!hasDraftChanges}
                >
                    {t('Reset')}
                </button>
                <button
                    type="button"
                    className={clsx(
                        'rounded-md px-2.5 py-1 text-xs font-medium',
                        hasDraftChanges
                            ? 'bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600'
                            : 'bg-gray-200 text-gray-500 cursor-not-allowed dark:bg-gray-800 dark:text-gray-500'
                    )}
                    onClick={() => {
                        if (!hasDraftChanges) {
                            return
                        }
                        applyDraft()
                        onApplied?.()
                    }}
                    disabled={!hasDraftChanges}
                >
                    {t('Apply')}
                </button>
            </div>
        </>
    )
}

export default UiTableColumnVisibilityPanel
